// The client drill-down at /clients/:id — the primary click target from the clients
// list, and where editing a client now happens.
//
// THREE tabs, three questions: who they are, what we do for them, and what that work has
// cost us.
//
// It was four, then two. Publications and agreements had tabs of their own, which put them level
// with the client's entire body of work while both are in fact properties of something
// further down — a publication belongs to a job, an agreement belongs to a retainer,
// and neither belongs to the client directly. They now live nested inside the work tab,
// under the retainer and job they actually hang off, so the tab strip is left holding
// only the things that really are client-level. See WorkTab.js for the tree, and
// CostsTab.js for why "what does this client cost us" is one of them.
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useAsync from '../../hooks/useAsync';
import { clientsApi } from '../../api';
import he from '../../i18n/he';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import Button from '../../components/Button';
import ClientModal from '../../components/ClientModal';
import DeleteClientModal from './DeleteClientModal';
import DetailsTab from './DetailsTab';
import WorkTab from './WorkTab';
import CostsTab from './CostsTab';
import MeetingsTab from './MeetingsTab';

// THREE tabs, and the third earns its place by asking a question the other two cannot.
// Details is who they are; work is what we do for them; costs is what that work has cost
// US — the absorbed spend that came out of our profit, which is the client-level question
// the money screen deliberately does not answer (it is scanned across clients, not down
// one). See CostsTab.
// FOUR tabs. Meetings is the newest and is the REVERSE VIEW OF THE CALENDAR: that screen is
// organised by date and answers "what is on Tuesday", this one is organised by client and
// answers "when do we next see them, and what was agreed last time". It sits after work
// because it is about the relationship rather than the billing, and it is display only —
// booking and summarising stay in the calendar.
const TABS = ['details', 'work', 'meetings', 'costs'];

// Keyed on the id so moving between clients remounts the view, matching JobDetail:
// the previous client's details must never sit under the new client's URL.
export default function ClientCard() {
  const { id } = useParams();
  return <ClientView key={id} id={id} />;
}

function ClientView({ id }) {
  const navigate = useNavigate();
  const { data: client, loading, error, reload } = useAsync(() => clientsApi.get(id), [id]);
  const [tab, setTab] = useState('details');
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Reversible and destroys nothing, so no confirmation dialog: the card simply
  // reloads and the button flips to say the opposite.
  const toggleActive = async (next) => {
    await clientsApi.setActive(id, next);
    reload();
  };

  return (
    <AsyncBoundary loading={loading} error={error}>
      {client && (
        <>
          {/* The badge is the answer to "why isn't this client in the list?" for anyone
              who arrived here from a task or a link rather than from the list itself. */}
          <PageHeader
            title={(
              <>
                {client.name}
                {!client.is_active && (
                  <span className="badge gray client-badge">
                    {he.clientCard.inactiveBadge}
                  </span>
                )}
              </>
            )}
          >
            <Button variant="subtle" small onClick={() => navigate('/clients')}>
              {he.clientCard.back}
            </Button>
          </PageHeader>

          <div className="seg client-tabs">
            {TABS.map((key) => (
              <Button
                key={key}
                small
                variant={tab === key ? '' : 'subtle'}
                onClick={() => setTab(key)}
              >
                {he.clientCard.tabs[key]}
              </Button>
            ))}
          </div>

          {tab === 'details' && (
            <DetailsTab
              client={client}
              onEdit={() => setEditing(true)}
              onDelete={() => setDeleting(true)}
              onToggleActive={toggleActive}
              onReload={reload}
            />
          )}
          {tab === 'work' && <WorkTab clientId={id} />}
          {tab === 'meetings' && <MeetingsTab clientId={id} />}
          {tab === 'costs' && <CostsTab clientId={id} />}

          {editing && (
            <ClientModal
              client={client}
              onClose={() => setEditing(false)}
              onSaved={() => { setEditing(false); reload(); }}
            />
          )}
          {/* After the delete this card's record no longer exists, so it cannot reload
              — it has to leave. `replace` so Back does not return to a URL that now
              404s. */}
          {deleting && (
            <DeleteClientModal
              client={client}
              onClose={() => setDeleting(false)}
              onDeleted={() => navigate('/clients', { replace: true })}
            />
          )}
        </>
      )}
    </AsyncBoundary>
  );
}
